import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import Icon from "../components/Icon";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

const ALERT_ICONS = {
    quota_exceeded: "AlertTriangle",
    payment_failed: "CreditCard",
    invalid_request: "AlertCircle",
    rate_limit: "Clock",
    auth_error: "ShieldAlert",
    service_unavailable: "ServerCrash",
};

export default function IntegrationAlerts({ onCountChange }) {
    const { t } = useTranslation();
    const { call: fetchAlerts, response: alerts } = useBluelagoon({
        method: "getIntegrationAlerts",
    });
    const { call: dismiss } = useBluelagoon({
        method: "dismissIntegrationAlert",
    });

    const lastFetchRef = React.useRef(0);
    useEffect(() => {
        const now = Date.now();
        if (now - lastFetchRef.current < 3 * 60 * 1000) return;
        lastFetchRef.current = now;
        fetchAlerts();
    }, []);

    useEffect(() => {
        onCountChange?.(alerts?.data?.length || 0);
    }, [alerts]);

    const handleDismiss = async (alertId) => {
        await dismiss({ alertId });
        fetchAlerts();
        toast.success(
            t("integrationAlerts.dismissed", {
                defaultValue: "Alert dismissed",
            })
        );
    };

    const handleDismissAll = async () => {
        await dismiss({ alertId: "all" });
        fetchAlerts();
        toast.success(
            t("integrationAlerts.allDismissed", {
                defaultValue: "All alerts dismissed",
            })
        );
    };

    if (!alerts?.data?.length) return null;

    return (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-amber-800 flex items-center gap-2">
                    <Icon name="Bell" className="w-4 h-4" />
                    {t("integrationAlerts.title", { defaultValue: "Alerts" })}
                    <span className="bg-amber-400 text-amber-900 text-xs px-2 py-0.5 rounded-full">
                        {alerts.data.length}
                    </span>
                </h3>
                <button
                    onClick={handleDismissAll}
                    className="text-xs text-amber-700 hover:text-amber-900 underline"
                >
                    {t("integrationAlerts.dismissAll", {
                        defaultValue: "Dismiss all",
                    })}
                </button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
                {alerts.data.map((alert) => (
                    <div
                        key={alert.id}
                        className="flex items-start gap-3 bg-white rounded-md p-3 border border-amber-100"
                    >
                        <Icon
                            name={ALERT_ICONS[alert.type] || "AlertCircle"}
                            className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900">
                                {alert.message}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                                {alert.integration && (
                                    <span className="text-xs text-gray-500">
                                        {alert.integration.name}
                                    </span>
                                )}
                                <span className="text-xs text-gray-400">
                                    {formatDistanceToNow(
                                        new Date(alert.createdAt),
                                        { addSuffix: true }
                                    )}
                                </span>
                            </div>
                        </div>
                        <button
                            onClick={() => handleDismiss(alert.id)}
                            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                        >
                            <Icon name="X" className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
